// Real client for sable-api (mini-brain-business-card-sites.2026-10-07 proposal §4 Phase 2,
// built/verified 2026-10-09 at apps/coremind/sable-api). Plain fetch, not the shared federated
// module pattern -- same reasoning as clientProposalsDirect.ts (this page has no need for any
// shared package's extra plumbing, and the federated shared-scope negotiation bug that forced that
// file's own direct-fetch approach is a real, open risk for any similar import here too).
export class SableApiError extends Error {
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = "SableApiError";
    }
}
// Same runtime-hostname pattern as calendarApiUrl.ts/clientProposalsDirect.ts (real bug found
// 2026-09-10 on those: a hardcoded local URL resolves to the VIEWER's own machine, not the dev
// box). sable-api now has a real public route (2026-10-10, Traefik IngressRoute `site-sable-api-dev`
// on this dev box, same local-dev-bridge pattern as sable's own site route) -- `/sable-api` below
// is that real path under ui.<env>.offsetmind.com.
function baseUrl() {
    if (window.location.hostname.startsWith("ui.")) {
        return `${window.location.origin}/sable-api`;
    }
    return window.__COREMIND_ENV__?.VITE_SABLE_API_URL ?? "http://localhost:8807";
}
async function parseErrorDetail(res) {
    try {
        const data = await res.json();
        return data?.detail ?? res.statusText;
    }
    catch {
        return res.statusText;
    }
}
export async function fetchDirectory(token) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return {
        status: body.status,
        slug: body.slug ?? null,
        ownerName: body.owner_name ?? null,
        business: body.business ?? null,
        tagline: body.tagline ?? null,
        accent: body.accent ?? null,
        hours: body.hours ?? null,
        address: body.address ?? null,
        phone: body.phone ?? null,
        links: body.links ?? [],
        scheduleLabel: body.schedule_label ?? null,
        presentationType: body.presentation_type ?? "chatbot",
        introAudioUrl: body.intro_audio_url ?? null,
    };
}
export async function listStories(token) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/stories`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return body.map((s) => ({
        documentId: s.document_id,
        title: s.title,
        scriptureReference: s.scripture_reference ?? null,
        topic: s.topic ?? null,
        series: s.series ?? null,
    }));
}
export async function getStory(token, documentId) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/stories/${documentId}`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return {
        documentId: body.document_id,
        title: body.title,
        series: body.series ?? null,
        scriptureReference: body.scripture_reference ?? null,
        scriptureText: body.scripture_text ?? null,
        storyText: body.story_text,
        closingLine: body.closing_line ?? null,
        audioUrl: body.audio_url ?? null,
        wordTimestamps: (body.word_timestamps ?? []).map((w) => ({ word: w.word, start: w.start, end: w.end })),
        hasSkit: Boolean(body.has_skit),
        lessonText: body.lesson_text ?? null,
        questions: (body.questions ?? []).map((q) => ({
            question: q.question,
            options: q.options ?? [],
            correctIndex: q.correct_index,
        })),
    };
}
export async function getSkit(token, documentId) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/stories/${documentId}/skit`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return {
        documentId: body.document_id,
        title: body.title,
        characters: body.characters ?? {},
        audioUrl: body.audio_url ?? null,
        lines: (body.lines ?? []).map((l) => ({
            character: l.character,
            text: l.text,
            start: l.start,
            end: l.end,
            wordTimestamps: (l.word_timestamps ?? []).map((w) => ({ word: w.word, start: w.start, end: w.end })),
        })),
    };
}
export async function fetchMiniSite(token) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/mini-site`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return {
        permitteeName: body.permittee_name,
        facilityLocation: body.facility_location,
        registrationNumber: body.registration_number,
        visits: (body.visits ?? []).map((v) => ({
            visitDate: v.visit_date,
            technician: v.technician ?? null,
            summary: v.summary,
            needsAttention: Boolean(v.needs_attention),
        })),
        paymentInfoText: body.payment_info_text,
    };
}
export async function sendChatMessage(token, message, visitorKey) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, visitor_key: visitorKey }),
    });
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return body.reply;
}
export async function synthesizeSpeech(token, text, visitorKey) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, visitor_key: visitorKey }),
    });
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    return res.blob();
}
// Pre-compiled "thinking" filler clips (voice_orb interface) -- not card/Assistant-scoped, so no
// `token`: same generic pool backs every Assistant, cached server-side after first synthesis.
export async function fetchFillerCount() {
    const res = await fetch(`${baseUrl()}/sable/filler-phrases/count`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    const body = await res.json();
    return body.count;
}
export async function fetchFillerAudio(index) {
    const res = await fetch(`${baseUrl()}/sable/filler-audio/${index}`);
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
    return res.blob();
}
export async function submitLead(token, name, contact, visitorKey) {
    const res = await fetch(`${baseUrl()}/sable/cards/${encodeURIComponent(token)}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, contact, visitor_key: visitorKey }),
    });
    if (!res.ok) {
        throw new SableApiError(res.status, await parseErrorDetail(res));
    }
}
